import { Lock, PenLine, ShieldCheck, Globe2, Clock } from "lucide-react";
import { Reveal } from "./Reveal";
import { Overline } from "./SectionHeading";
import { StatusBadge } from "./StatusBadge";
import { CREATOR_TRUST_PROOF } from "@/data/content";

/**
 * WhyCreators — trust-based social proof.
 *
 * IMPORTANT GUARDRAILS (do not violate):
 *   - No invented testimonials, no fake creator names, no earnings claims,
 *     no implied guaranteed results, no explicit imagery.
 *   - Only static trust cards are shown today.
 *
 * FUTURE (Codex + Supabase):
 *   Real, consented creator stories will be rendered in the "Creator stories
 *   coming soon" block below, pulled from a table like:
 *
 *     creator_testimonials(
 *       id, display_name, country, quote,
 *       consent_approved boolean,
 *       earnings_claim_included boolean,
 *       proof_required boolean,
 *       status, created_at
 *     )
 *
 *   Rendering rule: only show rows where consent_approved = true AND
 *   status = 'approved'. If earnings_claim_included = true, require
 *   proof_required verification before publishing.
 *
 * TODO (Supabase): fetch approved testimonials and replace the placeholder
 * block. Example:
 *   const { data } = await supabase
 *     .from('creator_testimonials')
 *     .select('*')
 *     .eq('consent_approved', true)
 *     .eq('status', 'approved');
 */
const iconMap = { lock: Lock, pen: PenLine, shield: ShieldCheck, globe: Globe2 };
const toneAccent = {
  positive: "text-ff-teal bg-ff-teal/12",
  neutral: "text-ff-ivory bg-white/8",
  ember: "text-ff-ember bg-ff-ember/12",
};

export const WhyCreators = () => (
  <section className="px-5 py-20 md:px-8 md:py-28" data-testid="why-creators">
    <div className="mx-auto max-w-7xl">
      <Reveal className="max-w-2xl">
        <Overline>Why creators choose FanFuel</Overline>
        <h2 className="mt-4 font-display text-4xl font-bold leading-tight tracking-tight text-ff-ivory md:text-5xl text-balance">
          Built for creator control.
        </h2>
        <p className="mt-5 text-base leading-relaxed text-ff-grey md:text-lg">
          Not success stories — safeguards. Here&rsquo;s what FanFuel is designed to protect.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {CREATOR_TRUST_PROOF.map((card, i) => {
          const Icon = iconMap[card.icon] || ShieldCheck;
          return (
            <Reveal key={card.title} delay={i * 0.08}>
              <div
                data-testid={`trust-proof-${i}`}
                className="h-full rounded-2xl border border-white/8 bg-ff-slate/60 p-7 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
              >
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${toneAccent[card.tone]}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-display text-lg font-semibold text-ff-ivory">{card.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ff-grey">{card.body}</p>
              </div>
            </Reveal>
          );
        })}
      </div>

      {/* Future-ready placeholder — real approved stories only, with consent + proof */}
      <Reveal delay={0.1} className="mt-8">
        <div
          data-testid="creator-stories-placeholder"
          className="flex flex-col items-start gap-5 rounded-3xl border border-dashed border-white/15 bg-ff-midnight/50 p-8 md:flex-row md:items-center md:p-10"
        >
          <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/5 text-ff-grey">
            <Clock className="h-6 w-6" />
          </span>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="font-display text-xl font-semibold text-ff-ivory md:text-2xl">
                Creator stories coming soon
              </h3>
              <StatusBadge tone="warning">Coming soon</StatusBadge>
            </div>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ff-grey md:text-base">
              As FanFuel launches, approved creator stories may appear here only with consent. No
              earnings claims will be published without proof and approval.
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default WhyCreators;
